import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { FiHome, FiDollarSign, FiUser, FiLogOut, FiMenu, FiClock, FiBell, FiUsers, FiCamera, FiCheckSquare, FiAlertCircle, FiInfo, FiRefreshCw, FiArrowLeft } from 'react-icons/fi';
import LanguageSwitcher from './LanguageSwitcher';
import FontSizeSwitcher from './FontSizeSwitcher';
import ChangePasswordModal from './ChangePasswordModal';
import ContributionStatusBanner from './ContributionStatusBanner';
import { getMyFollowupBadges, getNotifications, markNotificationRead, markAllNotificationsRead } from '../services/api';

const MemberLayout = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // v1.9.69 — Auto-close the mobile drawer on any route change + resize.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 768) setMobileOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [now, setNow] = useState(new Date());
  const [overdueCount, setOverdueCount] = useState(0);
  const [dueSoonCount, setDueSoonCount] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifUnread = notifs.filter(n => !n.read_at).length;

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const load = () => getNotifications().then(r => setNotifs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const [refreshing, setRefreshing] = React.useState(false);
  // v1.9.30 — top-bar refresh: fires 'app-refresh' event that pages opt into
  // to re-run their own load(). Spinner runs 800 ms for tactile feedback.
  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    try { window.dispatchEvent(new Event('app-refresh')); } catch (_) {}
    getNotifications().then(r => setNotifs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    setTimeout(() => setRefreshing(false), 800);
  };

  const openNotif = (n) => {
    if (!n.read_at) {
      markNotificationRead(n.id).catch(() => {});
      setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
    }
    setNotifOpen(false);
    if (n.link) navigate(n.link);
  };
  const markAllRead = () => {
    markAllNotificationsRead().catch(() => {});
    setNotifs(prev => prev.map(x => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
  };
  const timeAgo = (ts) => {
    if (!ts) return '';
    const d = new Date(ts.replace(' ', 'T') + 'Z');
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  useEffect(() => {
    if (user?.is_spiritual_father !== 1) return;
    const load = () => {
      getMyFollowupBadges()
        .then(r => { setOverdueCount(r.data?.overdue || 0); setDueSoonCount(r.data?.due_soon || 0); })
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [user?.is_spiritual_father]);

  const isActive = (path) => location.pathname === path;

  const NavItem = ({ path, icon, label }) => (
    <div
      className={`nav-item ${isActive(path) ? 'active' : ''}`}
      onClick={() => { navigate(path); if (window.innerWidth <= 768) setMobileOpen(false); }}
    >
      {icon}
      {!collapsed && <span>{label}</span>}
    </div>
  );

  const initials = user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'MB';

  return (
    <div className="app-layout">
      {mobileOpen && <div onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 99, display: window.innerWidth <= 768 ? 'block' : 'none' }} />}
      <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/logo.png" alt="Logo" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          </div>
          {!collapsed && (
            <div className="sidebar-brand">
              <h2>Member Portal</h2>
              <p>Church CMS</p>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          <NavItem path="/member/dashboard" icon={<FiHome className="nav-icon" />} label={t('nav.home')} />
          <NavItem path="/member/submit-payment" icon={<FiDollarSign className="nav-icon" />} label={t('nav.submitPayment')} />
          <NavItem path="/member/mark-attendance" icon={<FiCheckSquare className="nav-icon" />} label="Mark Attendance" />
          <NavItem path="/member/my-payments" icon={<FiClock className="nav-icon" />} label={t('nav.myPayments')} />
          <NavItem path="/member/subscriptions" icon={<FiBell className="nav-icon" />} label={t('nav.myContributions')} />
          {user?.is_spiritual_father === 1 && (
            <NavItem path="/member/my-members" icon={<FiUsers className="nav-icon" />} label={t('nav.myMembers')} />
          )}
          {user?.is_spiritual_father === 1 && (
            <NavItem path="/member/scan-followup" icon={<FiCamera className="nav-icon" />} label="Scan Follow-up" />
          )}
          <NavItem path="/member/info" icon={<FiInfo className="nav-icon" />} label={t('nav.information')} />
          <NavItem path="/member/profile" icon={<FiUser className="nav-icon" />} label={t('nav.myProfile')} />
          {/* Any staff user (Admin, Cashier, Finance, Secretary, Pastor,
              Cell Leader) who switched into the member portal can flip back. */}
          {user?.role && user.role !== 'member' && (
            <NavItem path="/dashboard" icon={<FiHome className="nav-icon" />}
              label={user.role === 'admin' ? 'Back to Admin' : 'Back to Staff Portal'} />
          )}
        </nav>

        {!collapsed && (
          <div className="sidebar-footer">
            <div className="user-info">
              <div className="user-avatar">{initials}</div>
              <div className="user-details">
                <h4>{user?.name}</h4>
                <p>Member</p>
              </div>
            </div>
            <button className="logout-btn" onClick={() => setShowPasswordModal(true)} style={{ marginBottom: 6 }}>
              🔒 {t('auth.changePassword')}
            </button>
            <button className="logout-btn" onClick={() => { logout(); navigate('/login'); }}>
              <FiLogOut /> {t('auth.logout')}
            </button>
          </div>
        )}
      </div>
      <ChangePasswordModal open={showPasswordModal} onClose={() => setShowPasswordModal(false)} />

      <div className="main-content">
        <div className="top-header">
          <div className="top-header-left">
            <button className="sidebar-toggle" onClick={() => {
              if (window.innerWidth <= 768) setMobileOpen(!mobileOpen);
              else setCollapsed(!collapsed);
            }}><FiMenu /></button>
            {/* v1.9.63 — Back button (hidden on the member dashboard root). */}
            {location.pathname !== '/member/dashboard' && location.pathname !== '/member' && (
              <button
                className="sidebar-toggle"
                onClick={() => { window.history.length > 1 ? navigate(-1) : navigate('/member/dashboard'); }}
                title="Back"
                aria-label="Back"
              ><FiArrowLeft /></button>
            )}
            <h2>Member Portal</h2>
          </div>
          <div className="top-header-right">
            {user?.is_spiritual_father === 1 && overdueCount > 0 && (
              <div
                onClick={() => navigate('/member/my-members')}
                title={`${overdueCount} member${overdueCount === 1 ? '' : 's'} overdue for follow-up`}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#991b1b', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
                onMouseLeave={e => e.currentTarget.style.background = '#fef2f2'}
              >
                <FiAlertCircle style={{ fontSize: 13 }} />
                <span>Overdue</span>
                <span style={{ background: '#dc2626', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>{overdueCount}</span>
              </div>
            )}
            {user?.is_spiritual_father === 1 && dueSoonCount > 0 && (
              <div
                onClick={() => navigate('/member/my-members')}
                title={`${dueSoonCount} follow-up${dueSoonCount === 1 ? '' : 's'} due in the next 7 days`}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#92400e', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                onMouseLeave={e => e.currentTarget.style.background = '#fffbeb'}
              >
                <FiBell style={{ fontSize: 13 }} />
                <span>Due soon</span>
                <span style={{ background: '#f59e0b', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>{dueSoonCount}</span>
              </div>
            )}
            {/* Notification bell */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setNotifOpen(o => !o)}
                title="Notifications"
                style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', padding: 6 }}
              >
                <FiBell style={{ fontSize: 20 }} />
                {notifUnread > 0 && (
                  <span style={{ position: 'absolute', top: -2, right: -2, background: '#dc2626', color: '#fff', borderRadius: '50%', minWidth: 16, height: 16, fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
                    {notifUnread > 9 ? '9+' : notifUnread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <>
                  <div onClick={() => setNotifOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 998 }} />
                  <div style={window.innerWidth <= 768
                    ? { position: 'fixed', top: 60, left: 10, right: 10, width: 'auto', maxHeight: '70vh', overflowY: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.18)', zIndex: 999 }
                    : { position: 'absolute', top: 36, right: 0, width: 340, maxHeight: 440, overflowY: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.18)', zIndex: 999 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #f3f4f6', position: 'sticky', top: 0, background: '#fff' }}>
                      <strong style={{ fontSize: 14 }}>Notifications</strong>
                      {notifUnread > 0 && <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: '#4f46e5', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Mark all read</button>}
                    </div>
                    {notifs.length === 0 ? (
                      <div style={{ padding: '24px 14px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No notifications</div>
                    ) : notifs.map(n => (
                      <div
                        key={n.id}
                        onClick={() => openNotif(n)}
                        style={{ padding: '11px 14px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: n.read_at ? '#fff' : '#eff6ff', display: 'flex', gap: 10 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = n.read_at ? '#fff' : '#eff6ff'}
                      >
                        {!n.read_at && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6', marginTop: 5, flexShrink: 0 }} />}
                        <div style={{ flex: 1, marginLeft: n.read_at ? 18 : 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{n.title}</div>
                          {n.body && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{n.body}</div>}
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{timeAgo(n.created_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh this page"
              style={{ background: refreshing ? '#eef2ff' : 'none', border: 'none', cursor: refreshing ? 'wait' : 'pointer', color: refreshing ? '#4f46e5' : '#374151', display: 'flex', alignItems: 'center', padding: 8, borderRadius: 8 }}
            >
              <FiRefreshCw style={{ fontSize: 18, animation: refreshing ? 'app-refresh-spin 0.8s linear infinite' : 'none' }} />
            </button>
            <FontSizeSwitcher compact />
            <LanguageSwitcher compact />
            <div className="date-display">
              <div>{now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
              <div>{now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}</div>
            </div>
          </div>
        </div>
        {/* v1.6.6 — portal-wide contribution status strip (red/amber/green) */}
        <div style={{ padding: '0 20px' }}>
          <ContributionStatusBanner />
        </div>
        <Outlet />
      </div>
    </div>
  );
};

export default MemberLayout;
